/**
 * Encodes a JSON-serializable object into Base64 and stores it in localStorage or sessionStorage.
 *
 * @template T - The type of the input data to be stored.
 * @param storage - Storage type or object ('local', 'session', or a Storage instance).
 * @param key - The key under which the data should be stored.
 * @param value - The object to encode and store.
 */
export const storeBase64StorageJSON = (storage: 'local' | 'session' | Storage, key: string, data: unknown): void => {
    const encoded = dataEncrypt(data);

    if (encoded !== null) {
        const dataStorage = resolveStorage(storage);
        dataStorage.setItem(key, encoded);
    } else {
        console.error(`[CMX] Error encoding base64 for ${getStorageName(storage)} key: "${key}"`);
    }
}

/**
 * Encodes data as a Base64 string.
 * @param data - Data to encode.
 * @returns Base64 string or null on error.
 */
export const dataEncrypt = (data: unknown): string => {
    return safeFn(
        () => {
            const json = typeof data === 'string' ? data : JSON.stringify(data);
            const bytes = new TextEncoder().encode(json);
            const binary = String.fromCharCode(...Array.from(bytes));
            return btoa(binary);
        },
        '[CMX] Error encoding base64 data'
    );
}

/**
 * Parses a Base64-encoded JSON object from localStorage or sessionStorage.
 *
 * @template T - The expected type of the parsed object.
 * @param storage - Storage type or object ('local', 'session', or a Storage instance).
 * @param key - The key under which the Base64-encoded data is stored.
 * @returns Parsed object of type T, or null if data is missing or invalid.
 */
export const parseBase64StorageJSON = <T>(storage: 'local' | 'session' | Storage, key: string): T | null => {
    const data = getDataFromStorage(storage, key);

    if (!data) {
        return null;
    }

    const errMessage = `[CMX] Error parsing base64 data from ${getStorageName(storage)} key: "${key}"`;
    const decoded = dataDecrypt(data, errMessage);

    if (!decoded) {
        return null;
    }

    return safeParse<T>(decoded);
}

/**
 * Decodes a base64-encoded string into a UTF-8 string.
 *
 * @param data - The base64-encoded string to decode. If not provided, the function returns `null`.
 * @param errMessage - Optional custom error message to use if decoding fails. If not provided, a default message is used.
 * @returns The decoded UTF-8 string, or `null` if `data` is not provided. If an error occurs during decoding, the error message is returned.
 */
export const dataDecrypt = (data?: string, errMessage?: string): string | null => {
    if (!data) {
        return null;
    }

    const defaultMessage = '[CMX] Error decoding base64 data';
    return safeFn(
        () => new TextDecoder().decode(Uint8Array.from(atob(data), c => c.charCodeAt(0))),
        errMessage ? errMessage : defaultMessage,
    );
}

/**
 * Parses a plain JSON object (not Base64-encoded) from localStorage or sessionStorage.
 *
 * @template T - The expected type of the parsed object.
 * @param storage - Storage type or object ('local', 'session', or a Storage instance).
 * @param key - The key under which the JSON string is stored.
 * @returns Parsed object of type T, or null if data is missing or invalid.
 */
export const parseFromStorage = <T>(storage: 'local' | 'session' | Storage, key: string): T | null => {
    const data = getDataFromStorage(storage, key);

    if (!data) {
        return null;
    }

    const errMessage = `[CMX] Error parsing data from ${getStorageName(storage)} key: "${key}"`;
    return safeParse<T>(data, errMessage);
}

/**
 * Safely parses a JSON string into an object, with optional error logging.
 *
 * @template T - The expected type of the parsed object.
 * @param data - JSON string to parse.
 * @param errMessage - Optional custom error message to log if parsing fails.
 * @returns Parsed object of type T, or null if parsing fails.
 */
export const safeParse = <T>(data?: string, errMessage?: string): T | null => {
    if (!data) {
        return null;
    }

    return safeFn(
        () => JSON.parse(data),
        errMessage ? errMessage : '[CMX] Error parsing data:'
    );
}

/**
 * Executes a function safely, returning its result or null if an error occurs.
 *
 * @template T - The return type of the executed function.
 * @param fn - The function to execute.
 * @param errMessage - Optional custom error message to log if execution fails.
 * @returns The result of the function if successful, or null if an error is thrown.
 */
export const safeFn = <T>(fn: () => T, errMessage?: string): T | null => {
    try {
        return fn();
    } catch (error) {
        const defaultMessage = '[CMX] Error executing function:';
        console.warn(errMessage ? errMessage : defaultMessage, error);
        return null;
    }
}

const getDataFromStorage = (storage: 'local' | 'session' | Storage, key: string): string | null => {
    const dataStorage = resolveStorage(storage);
    return dataStorage.getItem(key);
}

const getStorageName = (storage: 'local' | 'session' | Storage): 'LocalStorage' | 'SessionStorage' => {
    const isLocal = storage === 'local' || storage === localStorage;
    return isLocal ? 'LocalStorage' : 'SessionStorage';
}

const resolveStorage = (storage: 'local' | 'session' | Storage): Storage => {
    const isLocal = storage === 'local' || storage === localStorage;
    return isLocal ? localStorage : sessionStorage;
}
